import { Canvas } from "@/components/canvas";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { requireAuth } from "@/middleware/auth";
import { usePackQuery } from "@/queries/pack";

export const Route = createFileRoute("/pack/$pack_id")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();
	const { pack_id } = useParams({ from: Route.id });

	const queryOptions = usePackQuery(pack_id);
	const { data } = useQuery(queryOptions);

	const [slides, setSlides] = useState<ISlide[]>([]);
	const [isLoadingSlides, setIsLoadingSlides] = useState(true);

	const downloadImageToBase64 = useCallback(
		async (imageUrl: string): Promise<string> => {
			try {
				const response = await fetch(imageUrl);
				const blob = await response.blob();

				return new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => {
						resolve(reader.result as string);
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			} catch (error) {
				console.error("Error downloading image:", error);
				return "";
			}
		},
		[],
	);

	const loadSlides = useCallback(
		async (slides: ISlide[]) => {
			setIsLoadingSlides(true);
			// 이미지 슬라이드의 콘텐츠를 base64로 변환
			const processedSlides = await Promise.all(
				slides.map(async (slide) => {
					if (slide.type === "image") {
						const base64Content = await downloadImageToBase64(slide.data);
						return {
							...slide,
							content: base64Content,
						};
					}
					return slide;
				}),
			);

			setSlides(processedSlides);
			setIsLoadingSlides(false);

			return processedSlides;
		},
		[downloadImageToBase64],
	);

	const onClickBack = () => {
		const history = router.history;

		if (history.canGoBack()) {
			history.back();
		} else {
			router.navigate({ to: "/pack" });
		}
	};

	useEffect(() => {
		if (data) {
			loadSlides(data.slides);
		}
	}, [data, loadSlides]);

	return (
		<div className="relative size-full">
			{isLoadingSlides ? (
				<div className="flex justify-center items-center h-full">
					<Loader2 className="size-10 animate-spin text-blue-500" />
				</div>
			) : (
				<Canvas slides={slides} source_type="pack" target={pack_id} />
			)}

			{/* 뒤로가기 버튼 */}
			<Button
				variant="ghost"
				size="icon"
				className="absolute left-4 top-4 rounded-full flex items-center justify-center text-3xl bg-white/50 shadow-lg hover:bg-gray-100 disabled:opacity-50 z-30 p-2 size-14"
				onClick={onClickBack}
			>
				<ChevronLeft className="size-8 stroke-[1.5]" />
			</Button>
		</div>
	);
}
